import { dumpTriggerMessage, dumpLogDataMessage } from '@/messages'
import { jsonRpc, mediator, messenger } from '@communication'
import { extractLog } from '@logger'
import { useUserStore } from '@/stores'

// The signaling server closes a socket whose text frame is over 1 MiB chars (ServerletConfiguration), which drops the
// party out of the call: a log:dumpData frame carries up to 20 entries (extractLog), each cut to this length
const MAX_ENTRY_LENGTH = 5000
// A trigger within this time after the previous one is dropped (a flapping transport, repeated failures)
const TRIGGER_COOLDOWN = 30000
// Triggers per page load: every trigger makes each party of the call dump, and the dumps are kept
const MAX_TRIGGERS = 20

export default class LogProcedure {
  constructor() {
    this.name = 'LogProcedure'
    // { reason, error } of this party's own trigger, until log:dumpStart or the reply to the trigger
    this.pending = null
    this.lastTriggerAt = 0
    this.triggers = 0
  }
  install = () => {
    // The app emits the trigger on the messenger, the server sends log:dumpStart over the websocket (5ba58c7 had
    // swapped the two, so no dump was requested any more)
    messenger.subscribe(this.name, 'log:dumpTrigger', this.triggerDumpLog)
    jsonRpc.on('log:dumpStart', this.dumpLog)
  }
  // Compact, so the server logs a trigger's error on one line
  dumpObject = obj => {
    if (obj instanceof Error) {
      return obj.stack || String(obj)
    }
    try {
      return JSON.stringify(obj)
    } catch {
      return Object.prototype.toString.call(obj) // a circular structure
    }
  }
  // The server reads reason and error with getAsString(), which throws on an object (a JSON-RPC error,
  // { connectionState }): the request failed and neither log:dumpStart nor the data followed
  toText = value =>
    String(typeof value === 'object' && value !== null ? this.dumpObject(value) : value).slice(0, MAX_ENTRY_LENGTH)

  // Browser, OS and strategy of this party (no addresses), so a dump can be read without the other events
  context = () => {
    try {
      const { browser, os } = useUserStore().profile ?? {}
      return `context ${JSON.stringify({
        browser: [browser?.name, browser?.version].filter(Boolean).join(' '),
        os: [os?.name, os?.version].filter(Boolean).join(' '),
        strategy: mediator.strategy,
      })}`
    } catch {
      return ''
    }
  }

  // Runs synchronously inside messenger.emit: it must never throw into the emitter (a strategy's failure handling)
  triggerDumpLog = payload => {
    try {
      const { reason, error } = payload ?? {}
      const now = Date.now()
      if (this.pending || this.triggers >= MAX_TRIGGERS || now - this.lastTriggerAt < TRIGGER_COOLDOWN) return
      this.triggers++
      this.lastTriggerAt = now
      const pending = { reason: reason ?? 'unknown', error: error === undefined ? undefined : this.toText(error) }
      this.pending = pending
      // log:dumpStart arrives before this reply. A trigger without it (error reply, timeout, closed socket) must not
      // block the later dumps until the page is reloaded, nor label another party's dump with its reason
      jsonRpc.send(
        dumpTriggerMessage(pending.reason, pending.error),
        () => {
          if (this.pending === pending) this.pending = null
        },
        'no-log'
      )
    } catch (error) {
      this.pending = null
      console.warn('Log dump trigger failed', error)
    }
  }

  dumpLog = () => {
    const pending = this.pending
    this.pending = null
    try {
      // The server takes the IDs from the call; only a party that is no longer in one (feedback after the call)
      // needs to send them
      const callId = mediator.session?.configuration?.service?.callId
      const liteSessionId = mediator.session?.callId
      // The party that asked for the dump sends its reason even when nothing was collected (only unhandled rejections
      // are collected since 6e9287d), so every trigger leaves one record with the session IDs
      let first = !!pending
      let header = pending ? this.context() : ''
      while (true) {
        const entries = extractLog().map(item =>
          item
            .map(arg => (typeof arg === 'object' ? this.dumpObject(arg) : arg))
            .join(' ')
            .slice(0, MAX_ENTRY_LENGTH)
        )
        if (header) entries.unshift(header)
        header = ''
        const logToDump = entries.join('\n')
        if (!logToDump.length && !first) break
        first = false
        jsonRpc.send(
          dumpLogDataMessage(logToDump, pending?.reason, pending?.error, liteSessionId, callId),
          null,
          'no-log'
        )
      }
    } catch (error) {
      console.warn('Log dump failed', error)
    }
  }

  uninstall = () => {
    messenger.unsubscribe(this.name)
    jsonRpc.off('log:dumpStart', this.dumpLog)
  }
}
